import React, { useEffect, useState } from "react";
import { map } from "lodash";
import { ContainerProvider } from "@linkfluence/core/di";
import { Locale } from "@linkfluence/models/locale";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getWindowLocationParam } from "@tootools/http-utils";
import Empty from "@ui-kit/empty";
import Loading from "@ui-kit/splash-screen";
import {
  Preflight,
  ThemeProvider as XThemeProvider,
  defaultTheme,
} from "@xstyled/styled-components";
import { InsightPagesAPI } from "@radarly/api/insight-pages";
import { tr } from "@radarly/i18n";
import { isCardWithRequest } from "@radarly/models/cards/is";
import { isCardRow } from "@radarly/models/insight-pages/row";
import { PublicPage } from "@radarly/models/public-pages";
import { AppContext } from "@radarly/shared/context";
import LfReset from "../../../linkfluence/glossy/components/lf-reset";
import { getContainer } from "./container";
import Home from "./home";

// Force deploy

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      useErrorBoundary: undefined,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      suspense: false,
    },
  },
});

const checkRequests = (data: PublicPage): PublicPage => {
  let requestIdNb = 0;
  return {
    ...data,
    customPage: {
      ...data.customPage,
      //@ts-ignore;
      rows: map(data.customPage.rows, (row) => {
        return isCardRow(row)
          ? {
              ...row,
              cards: map(row.cards, (card) => {
                return card && isCardWithRequest(card)
                  ? {
                      ...card,
                      fragments: map(card.fragments || [], (fragment) => {
                        if (!fragment.request.id) {
                          fragment.request.id =
                            "generated_request_id_" + requestIdNb;
                          requestIdNb++;
                        }
                        return fragment;
                      }),
                    }
                  : card;
              }),
            }
          : row;
      }),
    },
  };
};

// url to test locally: http://localhost:3001/#/shared?s3id=e01eb274-ccb3-493f-b723-f789b5c2191f
export default function () {
  const [page, setPublicPage] = useState<PublicPage>(null);
  const [error, setError] = useState(null);
  const uid = getWindowLocationParam("uid");

  useEffect(() => {
    const api = new InsightPagesAPI();
    api
      .getShareInsightPage(uid)
      .then((res) => setPublicPage(checkRequests(res)))
      .catch((err) => {
        setError(err);
        throw new Error(err.message);
      });
  }, [uid]);

  if (error) {
    return (
      <>
        <LfReset />
        <Empty
          message={tr(
            Locale.English,
            "INSIGHT_PAGES::PAGE::SNAPSHOT_NOT_EXIST"
          )}
          isPage
        />
      </>
    );
  }

  return page ? (
    <QueryClientProvider client={queryClient}>
      <ContainerProvider container={getContainer(page)}>
        <XThemeProvider theme={{ ...defaultTheme }}>
          <LfReset />
          <Preflight />
          <AppContext>
            <Home snapshot={page} />
          </AppContext>
        </XThemeProvider>
      </ContainerProvider>
    </QueryClientProvider>
  ) : (
    <Loading />
  );
}
